/* WebRTC softphone for MMDSmart SIP trunking.
 *
 * SIP.js does the protocol; this module is the thin layer that keeps the rest
 * of the CRM from having to know anything about SIP. It is deliberately lazy:
 * sip.js is only imported when a call is actually placed, so agents who never
 * dial (and every page load before the first call) do not pay for it.
 */
import api from "./api";

let UA = null;          // SIP.js UserAgent
let registerer = null;
let session = null;
let cfg = null;
let audioEl = null;
const listeners = new Set();

export const onPhone = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const emit = (state) => listeners.forEach((f) => { try { f(state); } catch { /* listener's problem */ } });

let phone = { state: "idle", number: "", callId: null, since: null, muted: false, error: "", mode: "" };
const set = (patch) => { phone = { ...phone, ...patch }; emit(phone); };
export const getPhone = () => phone;

export async function loadConfig(force) {
  if (cfg && !force) return cfg;
  const { data } = await api.get("/telephony/config");
  cfg = data;
  return cfg;
}

function ensureAudio() {
  if (audioEl) return audioEl;
  audioEl = document.createElement("audio");
  audioEl.autoplay = true;
  audioEl.setAttribute("playsinline", "");   // iOS refuses inline audio without it
  audioEl.style.display = "none";
  document.body.appendChild(audioEl);
  return audioEl;
}

async function connect() {
  if (UA) return UA;
  const c = await loadConfig();
  if (!c.enabled) throw new Error("Calling is not switched on yet. An admin can configure it in Admin → Telephony.");
  if (!c.sip.configured) throw new Error("Your SIP credentials have not been set. Ask an admin to add them.");

  const { UserAgent, Registerer } = await import("sip.js");
  const uri = UserAgent.makeURI(`sip:${c.sip.username}@${c.domain}`);
  if (!uri) throw new Error("The SIP domain in the telephony settings is not valid.");

  const ice = [];
  if (c.stun) ice.push({ urls: c.stun });
  if (c.turn_url) ice.push({ urls: c.turn_url, username: c.turn_user, credential: c.turn_pass });

  UA = new UserAgent({
    uri,
    transportOptions: { server: c.ws_url },
    authorizationUsername: c.sip.username,
    authorizationPassword: c.sip.password,
    sessionDescriptionHandlerFactoryOptions: { peerConnectionConfiguration: { iceServers: ice } },
    delegate: {
      onInvite: (inv) => {                       // inbound call
        session = inv;
        set({ state: "incoming", number: inv.remoteIdentity?.uri?.user || "", error: "" });
        wireSession(inv);
      },
    },
  });
  await UA.start();
  registerer = new Registerer(UA);
  await registerer.register();
  return UA;
}

function attachMedia(s) {
  const pc = s.sessionDescriptionHandler?.peerConnection;
  if (!pc) return;
  const remote = new MediaStream();
  pc.getReceivers().forEach((r) => { if (r.track) remote.addTrack(r.track); });
  const el = ensureAudio();
  el.srcObject = remote;
  el.play().catch(() => { /* autoplay policy; the user gesture that dialled usually covers it */ });
}

function wireSession(s) {
  s.stateChange.addListener((st) => {
    if (st === "Established") { attachMedia(s); set({ state: "in-call", since: Date.now() }); mark("answered"); }
    if (st === "Terminated") { const was = phone.state; session = null; set({ state: "idle", number: "", since: null, muted: false, mode: "" });
      if (was === "in-call" || was === "calling") mark("ended"); }
  });
}

async function mark(status) {
  if (!phone.callId) return;
  const duration = phone.since ? Math.round((Date.now() - phone.since) / 1000) : 0;
  try { await api.patch(`/telephony/calls/${phone.callId}`, { status, duration }); } catch { /* logging only */ }
}

/** Place a call. `clientId` links it to a lead so the history shows up there. */
export async function call(number, clientId) {
  set({ error: "" });
  try {
    await connect();
    const { data } = await api.post("/telephony/calls", { to_number: number, client_id: clientId || null });
    const { UserAgent, Inviter } = await import("sip.js");
    const target = UserAgent.makeURI(`sip:${data.dial}@${cfg.domain}`);
    if (!target) throw new Error(`Could not dial ${data.dial}.`);
    session = new Inviter(UA, target, {
      sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
    });
    set({ state: "calling", number: data.to_number, callId: data.id, since: null, mode: "" });
    wireSession(session);
    await session.invite();
    return data;
  } catch (e) {
    set({ state: "idle", error: friendly(e) });
    throw new Error(friendly(e));
  }
}

/** Attach to another agent's live call: listen, whisper or join. */
export async function supervise(callId, mode) {
  set({ error: "" });
  try {
    await connect();
    const { data } = await api.get(`/telephony/supervise/${callId}`, { params: { mode } });
    const { UserAgent, Inviter } = await import("sip.js");
    const target = UserAgent.makeURI(`sip:${data.dial}@${cfg.domain}`);
    if (!target) throw new Error("The supervision feature code is not valid.");
    session = new Inviter(UA, target, {
      sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
    });
    set({ state: "calling", number: `${mode} · ${data.agent}`, callId: null, mode });
    wireSession(session);
    await session.invite();
    return data;
  } catch (e) {
    set({ state: "idle", error: friendly(e) });
    throw new Error(friendly(e));
  }
}

export async function answer() {
  if (session && phone.state === "incoming") {
    await session.accept({ sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } } });
  }
}

export async function hangup() {
  const s = session;
  if (!s) { set({ state: "idle" }); return; }
  try {
    const st = s.state;
    if (st === "Established") await s.bye();
    else if (st === "Initial" || st === "Establishing") {
      if (typeof s.cancel === "function") await s.cancel();
      else if (typeof s.reject === "function") await s.reject();
    }
  } catch { /* already gone */ }
  session = null;
  set({ state: "idle", number: "", since: null, muted: false, mode: "" });
}

export function toggleMute() {
  const pc = session?.sessionDescriptionHandler?.peerConnection;
  if (!pc) return;
  const next = !phone.muted;
  pc.getSenders().forEach((s) => { if (s.track && s.track.kind === "audio") s.track.enabled = !next; });
  set({ muted: next });
}

export async function disconnect() {
  try { if (registerer) await registerer.unregister(); } catch { /* ignore */ }
  try { if (UA) await UA.stop(); } catch { /* ignore */ }
  UA = null; registerer = null; session = null;
  set({ state: "idle" });
}

function friendly(e) {
  const m = String(e?.message || e || "");
  if (/getUserMedia|Permission denied|NotAllowedError/i.test(m))
    return "Microphone access was blocked. Allow it for this site and try again.";
  if (/NotFoundError|no microphone/i.test(m)) return "No microphone was found on this device.";
  if (/WebSocket|transport|connect/i.test(m))
    return "Could not reach the phone system. Check the SIP WebSocket URL in Admin → Telephony.";
  if (/401|403|Unauthorized|Forbidden/i.test(m)) return "The SIP credentials were rejected by the provider.";
  if (m.length > 160) return m.slice(0, 157) + "...";
  return m || "The call could not be placed.";
}
